// The consent page (`/connect`, docs/plans/claude-connector.md §3): an app (Claude) asked to act
// as the signed-in person, and the person approves or denies it here, in their own browser
// session.
//
// What the person checks before approving is where the access goes: the redirect host, in large
// type. The app's name is the app's own claim (its metadata document), so it is shown only as
// text. "Allow edits to drafts" starts unticked and is offered only when the app asked for edits
// and the role allows them (never to a Reporter). "Also let it prepare launches" (create new
// audiences, submit creatives for review) sits under it, unticked, usable only once edits are
// ticked, and is offered only when the app asked for it and the person's ceiling (role and the
// owner's switch) allows it. "Also let it administer SMART" (settings, alerts, remediations,
// invoice actions, users) sits under that the same way, offered only when the app asked for
// `sam:admin` and the ceiling covers Admin (an Admin with no production scope, both switches on);
// unticking a box unticks every box below it.
//
// The answer (`POST /oauth-consent/{id}/decision`) names the app's own address, carrying the
// one-time code or the refusal; the page goes there itself with `window.location.assign`, and
// only to the host it showed (`safeRedirectTarget`). A form post is out (the CSP's
// `form-action 'self'`), and the API never answers with a 3xx.

import { useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from '@tanstack/react-router';
import { api } from '@/api/client';
import { ApiError } from '@/api/errors';
import { useRegisteredMutation } from '@/api/mutation';
import { oauthConsent, sessionMe } from '@/api/resources';
import type { OauthConsentDecision, OauthConsentRedirect, OauthConsentView } from '@/api/types';
import { useMe } from '@/auth/useMe';
import { MutationErrorLine, QueryErrorNote } from '@/components/error-panel';
import { ACCESS_BLURB, ACCESS_LABEL, scopeCovers } from '@/lib/credentials';
import { safeRedirectTarget } from '@/lib/oauth-consent';
import type { User } from '@/lib/types';
import { RoleBadge } from './admin-shared';

const SECTION_LABEL = {
  fontFamily: 'var(--font-mono)',
  fontSize: 10,
  letterSpacing: '.14em',
  color: 'var(--ink-3)',
  textTransform: 'uppercase',
  margin: '0 0 6px',
  fontWeight: 400,
} as const;

const BODY = { margin: 0, fontSize: 13, lineHeight: 1.55, color: 'var(--ink-1)' } as const;

/** The answer pointed somewhere other than the host the page showed, so it stayed here. */
class RedirectRefused extends Error {
  constructor(readonly host: string) {
    super(`The answer pointed somewhere other than ${host}, so this page did not follow it.`);
    this.name = 'RedirectRefused';
  }
}

/** Leave the app for the app's own address: a top-level navigation, never a fetch. */
function leaveForApp(href: string): void {
  window.location.assign(href);
}

function Shell({ children }: { children: ReactNode }) {
  return (
    <div
      className="auth-shell"
      style={{ minHeight: '100vh', display: 'grid', placeItems: 'center', background: 'var(--bg)', padding: '24px 16px' }}
    >
      <main className="panel" style={{ width: '100%', maxWidth: 480, padding: 24 }}>
        <div className="mono muted" style={{ fontSize: 10.5, letterSpacing: '.14em', marginBottom: 10 }}>
          SHUBERT ADS MANAGER
        </div>
        {children}
      </main>
    </div>
  );
}

const TITLE = { fontSize: 22, margin: '0 0 8px' } as const;

/**
 * No request in the URL, or one that is answered or gone: start again from the app. `focus`
 * moves focus to the heading when this replaces the form the person was using, so a screen
 * reader hears what happened instead of losing its place.
 */
function NoRequest({ title, focus = false, children }: { title: string; focus?: boolean; children: ReactNode }) {
  const heading = useRef<HTMLHeadingElement>(null);
  useEffect(() => {
    if (focus) heading.current?.focus();
  }, [focus]);
  return (
    <Shell>
      <h1 ref={heading} tabIndex={-1} className="page-title" style={TITLE}>{title}</h1>
      <p style={{ ...BODY, marginBottom: 16 }}>{children}</p>
      <Link to="/" className="btn" style={{ width: '100%', justifyContent: 'center' }}>
        Go to the app
      </Link>
    </Shell>
  );
}

function Expired() {
  return (
    <NoRequest title="This request has ended" focus>
      It was already answered, or it waited longer than ten minutes. Nothing was connected by
      this page. Start again from the app you were connecting (Claude or ChatGPT): connect SMART
      there once more.
    </NoRequest>
  );
}

/** A notice that an admin has switched something off (as on Account). */
function SwitchedOff({ children }: { children: ReactNode }) {
  return (
    <div
      role="note"
      style={{
        padding: '10px 12px',
        margin: '0 0 16px',
        background: 'var(--bg-sunk)',
        border: '1px solid var(--line)',
        borderLeft: '3px solid var(--warn)',
        borderRadius: 4,
        fontSize: 12.5,
        color: 'var(--ink-1)',
        lineHeight: 1.5,
      }}
    >
      {children}
    </div>
  );
}

export interface ConnectScreenProps {
  /** The request the URL names; absent when it names none (or a malformed one). */
  requestId?: string;
  /** Where an answer goes; the page itself always leaves with `window.location.assign`. */
  leave?: (href: string) => void;
}

export function ConnectScreen({ requestId, leave = leaveForApp }: ConnectScreenProps) {
  if (!requestId) {
    return (
      <NoRequest title="No request to answer">
        This page opens when you connect Claude or ChatGPT to your account from the app itself. To
        connect one, add SMART as a custom connector in it and sign in from there.
      </NoRequest>
    );
  }
  return <ConsentRequest requestId={requestId} leave={leave} />;
}

function ConsentRequest({ requestId, leave }: { requestId: string; leave: (href: string) => void }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { data: me } = useMe();
  const consent = useQuery(oauthConsent(requestId));
  const view = consent.data;
  const decide = useDecision(requestId, view?.redirect_host ?? '', leave, () => {
    // Refused as switched off, at the cap, or because the browser's session now belongs to
    // someone else than the page showed: read the session and the request again, so the page
    // shows who is signed in and the switch as they now are.
    void queryClient.invalidateQueries({ queryKey: sessionMe.key(undefined) });
    void consent.refetch();
  });

  // A session that ended while the page was open: sign in again and come back here. The 401
  // handler (main.tsx) does the same for every request this page makes; this covers a failure
  // that reaches the screen first.
  const signedOut =
    (consent.error instanceof ApiError && consent.error.status === 401) ||
    (decide.error instanceof ApiError && decide.error.status === 401);
  useEffect(() => {
    if (signedOut) void navigate({ to: '/login', search: { next: 'connect', request: requestId } });
  }, [signedOut, navigate, requestId]);

  const gone =
    (consent.error instanceof ApiError && consent.error.status === 404) ||
    (decide.error instanceof ApiError && decide.error.status === 404);
  if (gone) return <Expired />;

  if (!view) {
    return (
      <Shell>
        <h1 className="page-title" style={TITLE}>Connect an app to your account</h1>
        {consent.isError && !signedOut ? (
          <QueryErrorNote error={consent.error} what="this request" onRetry={() => { void consent.refetch(); }} />
        ) : (
          <p className="muted" style={{ ...BODY, fontStyle: 'italic' }}>
            {signedOut ? 'Your session ended. Taking you to sign in…' : 'Loading the request…'}
          </p>
        )}
      </Shell>
    );
  }

  return (
    <Shell>
      <ConsentBody
        view={view}
        me={me ?? null}
        decide={decide}
        checking={consent.isFetching}
        onCheckAgain={() => { void consent.refetch(); }}
      />
    </Shell>
  );
}

type Decision = ReturnType<typeof useDecision>;

/** `POST /oauth-consent/{id}/decision`, then leave for the app's own address. */
function useDecision(
  requestId: string,
  expectedHost: string,
  leave: (href: string) => void,
  onConflict: () => void,
) {
  return useRegisteredMutation({
    // No data effect: the tab leaves the app the moment the answer lands, so there is no screen
    // left to refresh, and this page is on a public path that never takes part in the revision
    // manifest. The person's Account page reads the new grant fresh when they next open it.
    noDataEffect: true,
    mutationFn: async (decision: OauthConsentDecision) => {
      const answer = await api.post<OauthConsentRedirect>(
        `/oauth-consent/${encodeURIComponent(requestId)}/decision`,
        { body: decision },
      );
      const target = safeRedirectTarget(answer.redirect_to, expectedHost);
      if (!target) throw new RedirectRefused(expectedHost);
      return target;
    },
    onSuccess: (target) => leave(target.href),
    onError: (error) => {
      if (error instanceof ApiError && error.status === 409) onConflict();
    },
  });
}

function ConsentBody({
  view,
  me,
  decide,
  checking,
  onCheckAgain,
}: {
  view: OauthConsentView;
  /** The person the page shows as deciding; the decision names them (`signed_in_as`). */
  me: User | null;
  decide: Decision;
  checking: boolean;
  onCheckAgain: () => void;
}) {
  const [allowEdits, setAllowEdits] = useState(false);
  const [allowPrepare, setAllowPrepare] = useState(false);
  const [allowAdmin, setAllowAdmin] = useState(false);
  const allowId = useId();
  const allowBlurbId = useId();
  const prepareId = useId();
  const prepareBlurbId = useId();
  const adminId = useId();
  const adminBlurbId = useId();
  const hostId = useId();
  const whoId = useId();
  const accessId = useId();
  // Asking for Prepare asks for edits too; any ceiling above Read may be offered edits.
  const askedEdits = view.requested_scope !== 'read';
  const offerEdits = askedEdits && view.max_scope !== 'read';
  const offerPrepare = scopeCovers(view.requested_scope, 'prepare') && scopeCovers(view.max_scope, 'prepare');
  // Administering is offered only when the app asked for it and the ceiling covers it: the API's
  // `max_scope` is already the role's ceiling clamped by production scope and both switches.
  const askedAdmin = scopeCovers(view.requested_scope, 'admin');
  const offerAdmin = offerPrepare && askedAdmin && scopeCovers(view.max_scope, 'admin');
  // Nothing is decided before the page knows, and shows, who is deciding.
  const busy = decide.isPending || decide.isSuccess || !me;
  const conflict = decide.error instanceof ApiError && decide.error.status === 409;
  // The decision was refused because the session is someone else's now; the page re-read it.
  const personChanged = conflict && !!me && decide.variables?.signed_in_as !== me.id;

  const submit = (approve: boolean) => {
    if (!me) return;
    const edits = approve && offerEdits && allowEdits;
    const prepare = edits && offerPrepare && allowPrepare;
    decide.mutate({
      approve,
      signed_in_as: me.id,
      allow_edits: edits,
      allow_prepare: prepare,
      allow_admin: prepare && offerAdmin && allowAdmin,
    });
  };

  // Each box needs every box above it: unticking one unticks every box below it.
  const changeEdits = (checked: boolean) => {
    setAllowEdits(checked);
    if (!checked) {
      setAllowPrepare(false);
      setAllowAdmin(false);
    }
  };
  const changePrepare = (checked: boolean) => {
    setAllowPrepare(checked);
    if (!checked) setAllowAdmin(false);
  };

  return (
    <>
      <h1 className="page-title" style={TITLE}>Connect an app to your account</h1>
      <p style={{ ...BODY, marginBottom: 16 }}>
        <strong style={{ overflowWrap: 'anywhere' }}>{view.client_name}</strong> is asking to work
        with your account as you.
      </p>

      <section
        aria-labelledby={hostId}
        style={{
          padding: '12px 14px',
          marginBottom: 16,
          background: 'var(--bg-sunk)',
          border: '1px solid var(--line)',
          borderRadius: 4,
        }}
      >
        <h2 id={hostId} style={SECTION_LABEL}>You’ll be sent back to</h2>
        <div
          className="mono"
          data-testid="consent-redirect-host"
          style={{ fontSize: 26, fontWeight: 700, lineHeight: 1.2, color: 'var(--ink)', overflowWrap: 'anywhere' }}
        >
          {view.redirect_host}
        </div>
        <p className="muted" style={{ margin: '6px 0 0', fontSize: 12, lineHeight: 1.5 }}>
          Approve only if you started connecting from {view.redirect_host} just now.
        </p>
      </section>

      {me && (
        <section aria-labelledby={whoId} style={{ marginBottom: 16 }}>
          <h2 id={whoId} style={SECTION_LABEL}>Signed in as</h2>
          <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '4px 12px' }}>
            <span style={{ fontWeight: 650 }}>{me.name}</span>
            <span className="mono muted" style={{ fontSize: 11.5, overflowWrap: 'anywhere' }}>{me.email}</span>
            <RoleBadge role={me.role} />
          </div>
        </section>
      )}

      <section aria-labelledby={accessId} style={{ marginBottom: 16 }}>
        <h2 id={accessId} style={SECTION_LABEL}>What it can do</h2>
        <p style={BODY}>
          <strong>{ACCESS_LABEL.read}:</strong> {ACCESS_BLURB.read}
        </p>
        {offerEdits && (
          <div style={{ marginTop: 10 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <input
                id={allowId}
                type="checkbox"
                checked={allowEdits}
                disabled={busy}
                aria-describedby={allowBlurbId}
                onChange={(event) => changeEdits(event.target.checked)}
                style={{ width: 18, height: 18, margin: 0, flexShrink: 0 }}
              />
              <label htmlFor={allowId} style={{ fontSize: 13, fontWeight: 600, color: 'var(--ink)' }}>
                Allow edits to drafts
              </label>
            </div>
            <p id={allowBlurbId} className="muted" style={{ margin: '4px 0 0 26px', fontSize: 12, lineHeight: 1.5 }}>
              {ACCESS_BLURB.write}
            </p>
            {offerPrepare && (
              <div style={{ margin: '10px 0 0 26px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <input
                    id={prepareId}
                    type="checkbox"
                    checked={allowEdits && allowPrepare}
                    disabled={busy || !allowEdits}
                    aria-describedby={prepareBlurbId}
                    onChange={(event) => changePrepare(event.target.checked)}
                    style={{ width: 18, height: 18, margin: 0, flexShrink: 0 }}
                  />
                  <label htmlFor={prepareId} style={{ fontSize: 13, fontWeight: 600, color: 'var(--ink)' }}>
                    Also let it prepare launches
                  </label>
                </div>
                <p id={prepareBlurbId} className="muted" style={{ margin: '4px 0 0 26px', fontSize: 12, lineHeight: 1.5 }}>
                  {ACCESS_BLURB.prepare} A person still approves creatives and launches.
                </p>
                {offerAdmin && (
                  <div style={{ margin: '10px 0 0 26px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <input
                        id={adminId}
                        type="checkbox"
                        checked={allowEdits && allowPrepare && allowAdmin}
                        disabled={busy || !allowEdits || !allowPrepare}
                        aria-describedby={adminBlurbId}
                        onChange={(event) => setAllowAdmin(event.target.checked)}
                        style={{ width: 18, height: 18, margin: 0, flexShrink: 0 }}
                      />
                      <label htmlFor={adminId} style={{ fontSize: 13, fontWeight: 600, color: 'var(--ink)' }}>
                        Also let it administer SMART
                      </label>
                    </div>
                    <p id={adminBlurbId} className="muted" style={{ margin: '4px 0 0 26px', fontSize: 12, lineHeight: 1.5 }}>
                      {ACCESS_BLURB.admin}
                    </p>
                  </div>
                )}
              </div>
            )}
            {askedAdmin && !offerAdmin && (
              <p className="muted" style={{ margin: '10px 0 0 26px', fontSize: 12, lineHeight: 1.5 }}>
                It asked to administer SMART too. Only an Admin with access to every production can
                allow that, and only while an admin has switched it on, so it can’t here.
              </p>
            )}
          </div>
        )}
        {askedEdits && !offerEdits && (
          <p className="muted" style={{ margin: '6px 0 0', fontSize: 12, lineHeight: 1.5 }}>
            It asked to edit drafts too. Your role allows reading only, so it can read.
          </p>
        )}
        <p style={{ ...BODY, marginTop: 10 }}>
          Once connected, it acts as you even while you’re away (for example, working on a task
          you gave it), within what you allow here and never beyond what your role allows.
          You can disconnect it at any time on your{' '}
          <Link to="/account" style={{ color: 'var(--accent)', textDecoration: 'underline' }}>Account page</Link>.
        </p>
      </section>

      {!view.mcp_enabled && (
        <SwitchedOff>
          An admin has switched connected apps off, so this request can’t be approved until they
          are switched back on. You can still deny it.{' '}
          <button
            type="button"
            className="btn btn-sm"
            disabled={checking}
            onClick={onCheckAgain}
            style={{ marginLeft: 4 }}
          >
            {checking ? 'Checking…' : 'Check again'}
          </button>
        </SwitchedOff>
      )}

      <div role="status" aria-live="polite" style={{ fontSize: 12.5, lineHeight: 1.5, color: 'var(--ink-1)' }}>
        {decide.isSuccess && `Sending you back to ${view.redirect_host}…`}
      </div>
      {/* Always in the page, so a failed decision is announced when its words arrive. */}
      <div role="alert" style={{ fontSize: 12.5, lineHeight: 1.5, color: 'var(--bad)' }}>
        {decide.error instanceof RedirectRefused ? (
          <p style={{ margin: '0 0 10px' }}>
            {decide.error.message} Nothing was sent to the app. Start again from {view.client_name}.
          </p>
        ) : conflict ? (
          <p style={{ margin: '0 0 10px' }}>
            This wasn’t sent: {decide.error instanceof ApiError ? decide.error.message : ''}.
            {personChanged && ' The page now shows who is signed in: check it, then decide again.'}
          </p>
        ) : decide.error && !(decide.error instanceof ApiError && decide.error.status === 401) ? (
          <div style={{ marginBottom: 10 }}>
            <MutationErrorLine error={decide.error} />
          </div>
        ) : null}
      </div>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginTop: 6 }}>
        <button
          type="button"
          className="btn"
          disabled={busy}
          onClick={() => submit(false)}
          style={{ flex: '1 1 120px', justifyContent: 'center' }}
        >
          {decide.isPending && decide.variables?.approve === false ? 'Denying…' : 'Deny'}
        </button>
        <button
          type="button"
          className="btn btn-primary"
          disabled={busy || !view.mcp_enabled}
          title={view.mcp_enabled ? undefined : 'An admin has switched connected apps off'}
          onClick={() => submit(true)}
          style={{ flex: '1 1 120px', justifyContent: 'center' }}
        >
          {decide.isPending && decide.variables?.approve === true ? 'Approving…' : 'Approve'}
        </button>
      </div>
    </>
  );
}
